import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { QrCode } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";
import { PUBLIC_HOST } from "../lib/domain";

const ROLES = [
  { id: "parent", emoji: "👨‍👩‍👧", label: "Parent", desc: "Pour parents d'élèves. Suivi progression + billetterie + paiements.", color: "border-blue-500/40 bg-blue-500/5" },
  { id: "student", emoji: "🥋", label: "Élève", desc: "Pour élèves actifs. Portail personnel + boutique + compétitions.", color: "border-amber-500/40 bg-amber-500/5" },
  { id: "coach", emoji: "🎓", label: "Coach", desc: "Pour entraîneurs. CRM élèves + mini comps + debriefs anonymes.", color: "border-emerald-500/40 bg-emerald-500/5" },
  { id: "admin", emoji: "⚙️", label: "Admin / Kru", desc: "Pour Kru et co-admins. AI Operator + dashboard global.", color: "border-purple-500/40 bg-purple-500/5" },
  { id: "private_client", emoji: "💎", label: "Client privé", desc: "Pour coaching privé. Programme perso + sessions filmées.", color: "border-pink-500/40 bg-pink-500/5" },
  { id: "sponsor", emoji: "🏆", label: "Sponsor / Partenaire", desc: "Pour commanditaires. Engagements + ROI + livestream gratuit.", color: "border-yellow-500/40 bg-yellow-500/5" },
];

const SOURCE_PRESETS = [
  { v: "", l: "Aucune (lien standard)" },
  { v: "qr-card", l: "QR carte d'affaires" },
  { v: "email", l: "Email signature" },
  { v: "facebook", l: "Post Facebook" },
  { v: "instagram", l: "Story Instagram" },
  { v: "tiktok", l: "TikTok bio" },
  { v: "linkedin", l: "LinkedIn post" },
  { v: "dojo-poster", l: "Affiche au dojo" },
];

export default function OnboardingLinksModule() {
  const [lang, setLang] = useState("fr");
  const [source, setSource] = useState("qr-card");
  const [stats, setStats] = useState(null);

  const origin = window.location.origin;
  const buildUrl = (roleId) => {
    const params = new URLSearchParams();
    params.set("role", roleId);
    if (lang !== "fr") params.set("lang", lang);
    if (source) params.set("source", source);
    return `${origin}/onboarding?${params.toString()}`;
  };

  const loadStats = async () => {
    try {
      const r = await api.get("/onboarding/stats?days=30");
      setStats(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadStats(); }, []);

  const copyUrl = (url, label) => {
    navigator.clipboard.writeText(url);
    toast.success(`📋 Lien "${label}" copié`);
  };

  const downloadQR = (roleId, label) => {
    const svg = document.getElementById(`qr-${roleId}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = 1024;
      canvas.height = 1024;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 1024, 1024);
      ctx.drawImage(img, 0, 0, 1024, 1024);
      const png = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = png;
      a.download = `kaizen-qr-${roleId}${source ? "-" + source : ""}.png`;
      a.click();
      toast.success(`📥 QR "${label}" téléchargé`);
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  const roleCount = (roleId) => {
    if (!stats?.by_role) return 0;
    const r = stats.by_role.find((x) => x.role === roleId);
    return r ? r.count : 0;
  };

  return (
    <div className="space-y-6 p-6 md:p-8" data-testid="onboarding-links-module">
      {/* Header */}
      <div className="no-print">
        <PageHeader
          title="Carte d'affaires QR · Email signature · Posts"
          icon={QrCode}
          breadcrumbs={[{ label: "CRM & relations" }, { label: "Liens Onboarding QR" }]}
          subtitle="Génère un QR code unique par rôle (Parent / Élève / Coach / Sponsor…) pour ta carte d'affaires, ta signature email, tes posts. Chaque lien atterrit sur le flow onboarding adapté au visiteur."
          actions={
            <Button onClick={() => window.print()} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold whitespace-nowrap" data-testid="print-sheet-btn">
              🖨️ Imprimer planche A4
            </Button>
          }
        />
      </div>

      {/* Controls */}
      <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40 grid sm:grid-cols-2 gap-4 no-print">
        <div>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">LANGUE PAR DÉFAUT DU LIEN</div>
          <div className="flex gap-2">
            <button onClick={() => setLang("fr")} className={`flex-1 border rounded-lg p-2 text-sm transition ${lang === "fr" ? "border-amber-500/60 bg-amber-500/10" : "border-zinc-800 bg-black/30"}`} data-testid="lang-fr">🇫🇷 Français</button>
            <button onClick={() => setLang("en")} className={`flex-1 border rounded-lg p-2 text-sm transition ${lang === "en" ? "border-amber-500/60 bg-amber-500/10" : "border-zinc-800 bg-black/30"}`} data-testid="lang-en">🇬🇧 English</button>
          </div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">SOURCE DU LIEN (ANALYTICS)</div>
          <select value={source} onChange={(e) => setSource(e.target.value)} className="w-full bg-black border border-zinc-800 rounded-lg p-2 text-sm" data-testid="source-select">
            {SOURCE_PRESETS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </div>
      </div>

      {/* Stats */}
      {stats && stats.total_visits > 0 && (
        <div className="border border-emerald-500/30 rounded-2xl p-5 bg-emerald-500/5 no-print" data-testid="stats-block">
          <div className="text-[10px] tracking-[0.3em] text-emerald-400 mb-2">📊 STATS 30 DERNIERS JOURS</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Visites totales" value={stats.total_visits} accent />
            <Stat label="Top rôle" value={stats.by_role[0]?.role || "—"} />
            <Stat label="Top source" value={stats.by_source[0]?.source || "direct"} />
            <Stat label="Langues" value={stats.by_lang.map(l => `${l.lang}:${l.count}`).join(" / ")} />
          </div>
        </div>
      )}

      {/* Cards per role */}
      <div className="grid md:grid-cols-2 gap-4 no-print" data-testid="role-cards-grid">
        {ROLES.map((r) => {
          const url = buildUrl(r.id);
          return (
            <div key={r.id} className={`border rounded-2xl p-5 ${r.color}`} data-testid={`role-card-${r.id}`}>
              <div className="flex items-start gap-4">
                {/* QR */}
                <div className="bg-white p-2 rounded-lg shrink-0">
                  <QRCodeSVG
                    id={`qr-${r.id}`}
                    value={url}
                    size={120}
                    bgColor="#FFFFFF"
                    fgColor="#000000"
                    level="M"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-2xl">{r.emoji}</span>
                    <h3 className="text-lg font-bold">{r.label}</h3>
                    {roleCount(r.id) > 0 && (
                      <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-300">
                        {roleCount(r.id)} visites
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{r.desc}</p>
                  <div className="mt-3 text-[10px] text-zinc-500 font-mono truncate" title={url}>{url}</div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <button onClick={() => copyUrl(url, r.label)} className="text-[10px] bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 rounded-full px-3 py-1" data-testid={`copy-${r.id}`}>📋 Copier lien</button>
                    <button onClick={() => downloadQR(r.id, r.label)} className="text-[10px] bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 rounded-full px-3 py-1" data-testid={`download-qr-${r.id}`}>📥 Télécharger QR</button>
                    <a href={url} target="_blank" rel="noreferrer" className="text-[10px] bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-700 rounded-full px-3 py-1" data-testid={`test-${r.id}`}>👁️ Tester</a>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tips */}
      <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/30 no-print">
        <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">💡 OÙ UTILISER CES LIENS</div>
        <ul className="text-sm text-zinc-300 space-y-2 leading-relaxed">
          <li><strong>🎴 Carte d'affaires</strong> : QR Sponsor au verso → les sponsors potentiels scannent et arrivent direct sur leur flow personnalisé</li>
          <li><strong>📧 Signature email</strong> : Lien Parent dans tes réponses aux familles, lien Sponsor pour tes emails commerciaux B2B</li>
          <li><strong>📱 TikTok / IG bio</strong> : Lien Élève → conversion directe sans friction</li>
          <li><strong>🏢 Affiches dojo</strong> : QR Élève sur les murs du dojo → onboarding mobile instantané</li>
          <li><strong>🎯 Posts ciblés</strong> : Lien Sponsor sur LinkedIn, lien Parent sur Facebook groupes parents</li>
        </ul>
      </div>

      {/* Print sheet - hidden on screen, visible only on print */}
      <div className="print-sheet hidden">
        <div className="print-header">
          <div className="print-title">KAIZEN SYSTEM</div>
          <div className="print-subtitle">Planche QR Onboarding · Institut Impact Élite Martial</div>
        </div>
        <div className="print-grid">
          {ROLES.map((r) => (
            <div key={r.id} className="print-card">
              <div className="print-qr">
                <QRCodeSVG value={buildUrl(r.id)} size={180} bgColor="#FFFFFF" fgColor="#000000" level="M" />
              </div>
              <div className="print-role">{r.emoji} {r.label}</div>
              <div className="print-desc">{r.desc}</div>
              <div className="print-url">{buildUrl(r.id)}</div>
            </div>
          ))}
        </div>
        <div className="print-footer">
          Présenté par Institut Impact Élite Martial · KAIZEN System · {PUBLIC_HOST}
        </div>
      </div>

      <style>{`
        .print-sheet { display: none; }
        @media print {
          body * { visibility: hidden !important; }
          .print-sheet, .print-sheet * { visibility: visible !important; }
          .print-sheet {
            display: block !important;
            position: absolute !important;
            left: 0; top: 0; width: 210mm; min-height: 297mm;
            padding: 15mm;
            background: white !important; color: black !important;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          }
          .print-header { text-align: center; border-bottom: 2px solid #C8102E; padding-bottom: 10mm; margin-bottom: 12mm; }
          .print-title { font-size: 22pt; font-weight: 900; letter-spacing: 6px; color: #0E0E0E; }
          .print-subtitle { font-size: 9pt; color: #444; margin-top: 4px; letter-spacing: 1px; }
          .print-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8mm; }
          .print-card { border: 1px solid #ddd; border-radius: 8px; padding: 6mm; text-align: center; page-break-inside: avoid; }
          .print-qr { display: flex; justify-content: center; margin-bottom: 4mm; }
          .print-role { font-size: 14pt; font-weight: 700; color: #0E0E0E; margin-bottom: 2mm; }
          .print-desc { font-size: 8pt; color: #555; line-height: 1.4; margin-bottom: 3mm; min-height: 32px; }
          .print-url { font-size: 6pt; color: #888; word-break: break-all; font-family: monospace; }
          .print-footer { position: absolute; bottom: 10mm; left: 0; right: 0; text-align: center; font-size: 8pt; color: #888; letter-spacing: 1px; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-lg border p-2 ${accent ? "border-emerald-500/40 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950/50"}`}>
      <div className="text-[9px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className={`text-lg font-bold mt-1 truncate ${accent ? "text-emerald-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}
